import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import MaleProfilePhoto from "../../../../assets/images/male-doc.png";
import FemaleProfilePhoto from "../../../../assets/images/female-doc.webp";
import TitleSectionComponent from "../../../../components/core-components/titleSection";
import { useQuery } from "@tanstack/react-query";
import { MemberService } from "../../../../network/MemberService";
import { useNavigate } from "react-router-dom";
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

// First, let's add interface definitions at the top of the file
interface Member {
  _id: string;
  name: string;
  surname: string;
  memberNumber?: string;
  title?: string;
  role?: string;
  position?: string;
  gender?: string;
  image?: string;
  institutions: Institution[];
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  boards?: {
    management?: {
      isSelected: boolean;
      role: string;
      position?: string;
    };
    disciplinary?: {
      isSelected: boolean;
      role: string;
      position?: string;
    };
    audit?: {
      isSelected: boolean;
      role: string;
      position?: string;
    };
  };
  isDirectorOfOnboarding?: boolean;
}

interface Institution {
  name: string;
  address: string;
}

const MemberCard = styled(Card)(({ theme }) => ({
  textAlign: "center",
  borderRadius: "16px",
  boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)",
  },
  height: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
  },
  [theme.breakpoints.between("sm", "md")]: {
    maxWidth: "450px",
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: "280px", // Increased from 240px to 280px
  },
}));

const LocationBox = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--red-primary, #B71C1C)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1),
  margin: "0 16px",
  marginTop: "-20px",
  position: "relative",
  zIndex: 1,
}));

const getBoardRole = (member: any) => {
  try {
    if (member?.boards?.management?.isSelected) {
      const role = member.boards.management.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      let position = member.boards.management.position || '';
      
      // Transform position titles to shorter versions
      position = position
        .replace('Yönetim Kurulu Genel Sekreteri', 'Genel Sekreter')
        .replace('Yönetim Kurulu Başkan Yardımcısı', 'Başkan Yardımcısı')
        .replace('Yönetim Kurulu Sayman', 'Sayman');

      return position;
    }
    if (member?.boards?.disciplinary?.isSelected) {
      const role = member.boards.disciplinary.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      const position = member.boards.disciplinary.position ? `${member.boards.disciplinary.position}  ` : '';
      return `${position}`;
    }
    if (member?.boards?.audit?.isSelected) {
      const role = member.boards.audit.role === "Main Member" ? "Asıl Üye" : "Yedek Üye";
      const position = member.boards.audit.position ? `${member.boards.audit.position}  ` : '';
      return `${position}`;
    }
    return "";
  } catch (error) {
    console.error("Error getting board role:", error);
    return "";
  }
};

const Kurullar = () => {
  const [expanded, setExpanded] = useState<string | false>("yonetim");
  const navigate = useNavigate();

  const memberService = new MemberService();
  const fetchMembers = async (params: any) => {
    const res = await memberService.getAll(params);
    return res?.data;
  };

  const { data: boardsManagementmembersList } = useQuery(
    ["boardsManagementmembersList"],
    () => fetchMembers({ management: true })
  );

  const { data: boardsAuditmembersList } = useQuery(
    ["boardsAuditmembersList"],
    () => fetchMembers({ audit: true })
  );

  const { data: boardsDisciplinarymembersList } = useQuery(
    ["boardsDisciplinarymembersList"],
    () => fetchMembers({ disciplinary: true })
  );

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  type BoardPosition = 'Yönetim Kurulu Başkanı' | 'Yönetim Kurulu Genel Sekreteri' | 'Yönetim Kurulu Başkan Yardımcısı' | 'Yönetim Kurulu Sayman' | 'Yönetim Kurulu Üyesi';

  const POSITION_ORDER: Record<BoardPosition, number> = {
    'Yönetim Kurulu Başkanı': 1,
    'Yönetim Kurulu Genel Sekreteri': 2,
    'Yönetim Kurulu Başkan Yardımcısı': 3,
    'Yönetim Kurulu Sayman': 4,
    'Yönetim Kurulu Üyesi': 5
  };

  const kurullar = {
    yonetim: {
      baslik: "Yönetim Kurulu",
      aciklama: "Yönetim kurulumuz derneğin idari işlerini yürütür.",
      uyeler: boardsManagementmembersList?.sort((a: Member, b: Member) => {
        const positionA = (a.boards?.management?.position || '') as BoardPosition;
        const positionB = (b.boards?.management?.position || '') as BoardPosition;
        
        const positionOrderA = POSITION_ORDER[positionA] || 999;
        const positionOrderB = POSITION_ORDER[positionB] || 999;
        
        if (positionOrderA !== positionOrderB) {
          return positionOrderA - positionOrderB;
        }
        
        const aNumber = a.memberNumber ? parseInt(a.memberNumber) : Infinity;
        const bNumber = b.memberNumber ? parseInt(b.memberNumber) : Infinity;
        return aNumber - bNumber;
      }),
    },
    denetim: {
      baslik: "Denetim Kurulu",
      aciklama: "Denetim kurulumuz derneğin mali ve idari denetimini gerçekleştirir.",
      uyeler: boardsAuditmembersList?.sort((a: Member, b: Member) => {
        const positionA = a.boards?.audit?.position || '';
        const positionB = b.boards?.audit?.position || '';
        
        if (positionA.includes('Başkan') && !positionB.includes('Başkan')) return -1;
        if (!positionA.includes('Başkan') && positionB.includes('Başkan')) return 1;
        
        const aNumber = a.memberNumber ? parseInt(a.memberNumber) : Infinity;
        const bNumber = b.memberNumber ? parseInt(b.memberNumber) : Infinity;
        return aNumber - bNumber;
      }),
    },
    disiplin: {
      baslik: "Disiplin Kurulu",
      aciklama: "Disiplin kurulumuz dernek üyelerinin disiplin işlemlerini yürütür.",
      uyeler: boardsDisciplinarymembersList?.sort((a: Member, b: Member) => {
        const aNumber = a.memberNumber ? parseInt(a.memberNumber) : Infinity;
        const bNumber = b.memberNumber ? parseInt(b.memberNumber) : Infinity;
        return aNumber - bNumber;
      }),
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: "var(--bg-color, #f5f5f5)",
        minHeight: "100vh",
        py: 4,
        overflow: "hidden",
        margin: { xs: "-10px", md: "50px" },
        marginTop: "-20px",
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 1200, md: 1600, lg: 1800 }, 
          margin: "0 auto",
          px: { xs: 2, md: 5 },
          mt: 5,
        }}
      >
        <Box
          sx={{
            marginTop: "-40px",
            marginBottom: "20px",
            "& h2": {
              fontSize: { xs: "24px", md: "32px" },
              fontWeight: 600,
              textAlign: "center",
            }
          }}
        >
          <TitleSectionComponent titleText="Kurullar" />
        </Box>

        {Object.entries(kurullar).map(([key, kurul]) => (
          <Accordion
            key={key}
            expanded={expanded === key}
            onChange={handleChange(key)}
            sx={{
              mb: 2,
              boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "white",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#f8f8f8",
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {kurul.baslik}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph color="text.secondary" sx={{ mb: 4 }}>
                {kurul.aciklama}
              </Typography>

              <Grid
                container
                spacing={2} // Reduced from 3 to 2
                sx={{
                  justifyContent: "center",
                  px: { xs: 1, sm: 2 }, // Reduced padding
                  mx: { xs: -1, sm: -2 }, // Adjusted margin to match padding
                }}
              >
                {kurul.uyeler?.map((uye: any) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={2.4} // Changed from md={3} to md={2.4} for 5 columns
                    key={uye.id}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      p: 1, // Reduced from 2 to 1
                    }}
                  >
                    <MemberCard>
                      <Box position="relative">
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "75%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            onClick={() =>
                              navigate(
                                `/uyeler/${uye._id}/${uye.name}-${uye.surname}`
                              )
                            }
                            alt={uye.name}
                            src={
                              uye.image ||
                              (uye.gender?.toLowerCase() === "male"
                                ? MaleProfilePhoto
                                : FemaleProfilePhoto)
                            }
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              borderRadius: "16px 16px 0 0",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                          />
                        </Box>
                        {uye.isDirectorOfOnboarding && (
                          <Box
                            position="absolute"
                            top={8}
                            right={8}
                            bgcolor="rgba(255, 255, 255, 0.8)"
                            borderRadius="50%"
                            padding="4px"
                          >
                            <Tooltip title={uye.role || null}>
                              <StarRateRoundedIcon style={{ color: "silver" }} />
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                      <LocationBox>
                    <Typography variant="body1" sx={{ color: "#fff" }}>
                      {getBoardRole(uye)}
                    </Typography>
                  </LocationBox>
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          padding: "16px",
                          "&:last-child": {
                            paddingBottom: "16px",
                          },
                        }}
                        onClick={() =>
                          navigate(
                            `/uyeler/${uye._id}/${uye.name}-${uye.surname}`
                          )
                        }
                      >
                        <Stack spacing={1}>
                          <Typography variant="h6" component="div">
                            {uye.title} {uye.name} {uye.surname}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {React.Children.toArray(
                              uye.institutions.map(
                                (institution: {
                                  name: string;
                                  address: string;
                                }) => institution.name
                              )
                            ).join(" / ")}
                          </Typography>
                         {/*  <Typography variant="body2" color="text.primary">
                            {uye.role}
                          </Typography> */}
                        </Stack>
                      </CardContent>
                      <Box mb={2}>
                        {uye.socialMedia?.linkedin && (
                          <IconButton
                            component="a"
                            href={uye.socialMedia.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LinkedInIcon />
                          </IconButton>
                        )}

                        {uye.socialMedia?.twitter && (
                          <IconButton
                            component="a"
                            href={uye.socialMedia.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <TwitterIcon />
                          </IconButton>
                        )}

                        {uye.socialMedia?.facebook && (
                          <IconButton
                            component="a"
                            href={uye.socialMedia.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FacebookIcon />
                          </IconButton>
                        )}

                        {uye.socialMedia?.instagram && (
                          <IconButton
                            component="a"
                            href={uye.socialMedia.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <InstagramIcon />
                          </IconButton>
                        )}

                        {uye.socialMedia?.youtube && (
                          <IconButton
                            component="a"
                            href={uye.socialMedia.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <YouTubeIcon />
                          </IconButton>
                        )}
                      </Box>
                    </MemberCard>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default Kurullar;
